import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { dpsTheme } from '../../../styles/dpsTheme';
import { useDeviceSize } from '../../../hooks/useMediaQuery';

interface Tab {
  id: string;
  label: string;
  path: string;
  icon: JSX.Element;
}

interface DPSNavigationProps {
  reportId: string;
  isOpen?: boolean;
  onClose?: () => void;
}

export const DPSNavigation: React.FC<DPSNavigationProps> = ({ reportId, isOpen = true, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isMobile } = useDeviceSize();

  const tabs: Tab[] = [
    {
      id: 'overview',
      label: "Vue d'ensemble",
      path: `/dps/report/${reportId}`,
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="3" width="7" height="7" />
          <rect x="14" y="3" width="7" height="7" />
          <rect x="14" y="14" width="7" height="7" />
          <rect x="3" y="14" width="7" height="7" />
        </svg>
      ),
    },
    {
      id: 'analysis',
      label: 'Analyse détaillée',
      path: `/dps/report/${reportId}/analysis`,
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="18" y1="20" x2="18" y2="10" />
          <line x1="12" y1="20" x2="12" y2="4" />
          <line x1="6" y1="20" x2="6" y2="14" />
        </svg>
      ),
    },
    {
      id: 'actions',
      label: 'Mes actions',
      path: `/dps/report/${reportId}/actions`,
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="9 11 12 14 22 4" />
          <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
        </svg>
      ),
    },
    {
      id: 'history',
      label: 'Historique',
      path: `/dps/report/${reportId}/history`,
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10" />
          <polyline points="12 6 12 12 16 14" />
        </svg>
      ),
    },
  ];

  const isActive = (path: string) => location.pathname === path;

  if (isMobile) {
    return (
      <nav
        style={{
          position: 'fixed',
          top: '60px',
          left: 0,
          right: 0,
          bottom: 0,
          background: dpsTheme.colors.background.darker,
          backdropFilter: 'blur(20px)',
          display: isOpen ? 'flex' : 'none',
          flexDirection: 'column',
          gap: 0,
          zIndex: 100,
          overflow: 'auto',
          paddingTop: '16px',
          paddingBottom: '16px',
          boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.5)',
        }}
      >
        {tabs.map((tab) => (
          <div
            key={tab.id}
            onClick={() => {
              navigate(tab.path);
              if (onClose) onClose();
            }}
            style={{
              padding: '20px 24px',
              fontWeight: 500,
              fontSize: '16px',
              color: isActive(tab.path) ? dpsTheme.colors.accent.blue.primary : dpsTheme.colors.text.secondary,
              cursor: 'pointer',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              borderLeft: isActive(tab.path) ? `4px solid ${dpsTheme.colors.accent.blue.primary}` : '4px solid transparent',
              paddingLeft: isActive(tab.path) ? '20px' : '24px',
              background: isActive(tab.path) ? 'rgba(59, 130, 246, 0.15)' : 'transparent',
              borderBottom: `1px solid ${dpsTheme.colors.border.secondary}`,
            }}
            onMouseEnter={(e) => {
              if (!isActive(tab.path)) {
                e.currentTarget.style.color = dpsTheme.colors.text.primary;
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isActive(tab.path)) {
                e.currentTarget.style.color = dpsTheme.colors.text.secondary;
                e.currentTarget.style.background = 'transparent';
              }
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', color: 'inherit' }}>
              {tab.icon}
            </span>
            <span style={{ color: 'inherit' }}>{tab.label}</span>
          </div>
        ))}
      </nav>
    );
  }

  return (
    <nav
      style={{
        background: dpsTheme.colors.background.darker,
        backdropFilter: 'blur(20px)',
        borderBottom: `1px solid ${dpsTheme.colors.border.primary}`,
        padding: '0 48px',
      }}
    >
      <div style={{ display: 'flex', gap: '4px', maxWidth: '1400px', margin: '0 auto' }}>
        {tabs.map((tab) => (
          <div
            key={tab.id}
            onClick={() => navigate(tab.path)}
            style={{
              padding: '16px 24px',
              fontWeight: 500,
              fontSize: '14px',
              color: isActive(tab.path) ? dpsTheme.colors.accent.blue.secondary : dpsTheme.colors.text.muted,
              cursor: 'pointer',
              transition: 'all 0.2s',
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              borderBottom: isActive(tab.path) ? '2px solid transparent' : 'none',
              backgroundImage: isActive(tab.path)
                ? 'linear-gradient(90deg, #3b82f6, #2563eb)'
                : 'none',
              backgroundPosition: 'bottom',
              backgroundSize: '100% 2px',
              backgroundRepeat: 'no-repeat',
            }}
            onMouseEnter={(e) => {
              if (!isActive(tab.path)) {
                e.currentTarget.style.color = dpsTheme.colors.text.secondary;
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isActive(tab.path)) {
                e.currentTarget.style.color = dpsTheme.colors.text.muted;
                e.currentTarget.style.background = 'transparent';
              }
            }}
          >
            {tab.icon}
            {tab.label}
          </div>
        ))}
      </div>
    </nav>
  );
};
